// ========================================================================
// FILE: src/pages/Mitra/DaftarPelanggan.jsx
// PENJELASAN: Halaman untuk mitra mengelola daftar pelanggan mereka
// ========================================================================
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { addCustomer, getCustomersByMitra, updateCustomer, deleteCustomer } from '../../supabase/database';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaPhone, FaMapMarkerAlt, FaUpload, FaDownload } from 'react-icons/fa';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import Papa from 'papaparse';

const DaftarPelanggan = () => {
  const { currentUser } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    nama_pelanggan: '',
    no_wa: '',
    alamat: ''
  });

  // CSV Import states
  const fileInputRef = useRef(null);
  const [parsedCustomerData, setParsedCustomerData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    console.log('🔍 DaftarPelanggan - useEffect triggered, currentUser:', {
      uid: currentUser?.uid,
      id: currentUser?.id,
      email: currentUser?.email,
      userObject: currentUser
    });
    if (currentUser?.uid) {
      fetchCustomers();
    } else {
      console.log('No currentUser.uid, skipping fetch');
      setLoading(false);
    }
  }, [currentUser?.uid]); // More specific dependency

  const fetchCustomers = async () => {
    if (!currentUser?.uid) {
      console.log('No currentUser.uid available');
      return;
    }
    
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      console.log('Fetching customers for mitra:', currentUser.uid);
      const customerList = await getCustomersByMitra(currentUser.uid);
      console.log('Customers fetched successfully:', customerList);
      setCustomers(customerList);
    } catch (error) {
      console.error('Error fetching customers:', error);
      console.error('Error details:', error.message, error.code);
      setError(`Gagal mengambil data pelanggan: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nama_pelanggan.trim()) {
      errors.nama_pelanggan = 'Nama pelanggan harus diisi';
    } else if (formData.nama_pelanggan.trim().length < 2) {
      errors.nama_pelanggan = 'Nama pelanggan minimal 2 karakter';
    }
    
    if (!formData.no_wa.trim()) {
      errors.no_wa = 'Nomor WhatsApp harus diisi';
    } else {
      const phoneRegex = /^(\+62|62|08)[0-9]{8,13}$/;
      if (!phoneRegex.test(formData.no_wa)) {
        errors.no_wa = 'Format nomor WhatsApp tidak valid (contoh: 08123456789)';
      }
    }
    
    if (!formData.alamat.trim()) {
      errors.alamat = 'Alamat harus diisi';
    } else if (formData.alamat.trim().length < 10) {
      errors.alamat = 'Alamat minimal 10 karakter';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const customerData = {
        ...formData,
        mitraUid: currentUser.uid,
        nama_mitra: currentUser.nama || currentUser.nama_mitra,
        kode_mitra: currentUser.kode_mitra
      };

      console.log('Saving customer data:', customerData);

      if (editingCustomer) {
        console.log('🔄 Updating customer with ID:', editingCustomer.id);
        console.log('🔄 Update payload:', customerData);
        await updateCustomer(editingCustomer.id, customerData);
        setMessage('Data pelanggan berhasil diperbarui');
      } else {
        console.log('🆕 Adding new customer:', customerData);
        await addCustomer(customerData);
        setMessage('Pelanggan baru berhasil ditambahkan');
      }

      resetForm();
      
      // Fetch customers again after successful save
      await fetchCustomers();
      
      // Auto hide success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving customer:', error);
      console.error('Error details:', error.message, error.code);
      setError(`Gagal menyimpan data pelanggan: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (customer) => {
    console.log('🔄 handleEdit: Editing customer:', customer);
    setEditingCustomer(customer);
    setFormData({
      nama_pelanggan: customer.nama || customer.nama_pelanggan || '',
      no_wa: customer.no_wa || '',
      alamat: customer.alamat || ''
    });
    console.log('🔄 handleEdit: Form data set:', {
      nama_pelanggan: customer.nama || customer.nama_pelanggan || '',
      no_wa: customer.no_wa || '',
      alamat: customer.alamat || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus pelanggan ini?')) {
      return;
    }

    try {
      await deleteCustomer(customerId);
      setMessage('Pelanggan berhasil dihapus');
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      setError('Gagal menghapus pelanggan');
    }
  };

  const resetForm = () => {
    setFormData({
      nama_pelanggan: '',
      no_wa: '',
      alamat: ''
    });
    setFormErrors({});
    setEditingCustomer(null);
    setIsModalOpen(false);
    setError('');
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  // CSV Import Functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setParsedCustomerData([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: header => header.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        const requiredHeaders = ['nama_pelanggan', 'no_wa', 'alamat'];
        const actualHeaders = results.meta.fields;
        const missingHeaders = requiredHeaders.filter(h => !actualHeaders.includes(h));

        if (missingHeaders.length > 0) {
          setError(`Header CSV tidak valid. Kolom yang diperlukan: ${requiredHeaders.join(', ')}`);
          return;
        }

        // Validate and process data
        const validData = results.data.filter(row => 
          row.nama_pelanggan && row.no_wa && row.alamat &&
          row.nama_pelanggan.trim().length >= 2 &&
          row.no_wa.trim().length >= 8 &&
          row.alamat.trim().length >= 10
        ).map(row => ({
          nama_pelanggan: String(row.nama_pelanggan).trim(),
          no_wa: String(row.no_wa).trim(),
          alamat: String(row.alamat).trim()
        }));

        if (validData.length === 0) {
          setError('Tidak ada data valid untuk diimpor. Pastikan semua kolom diisi dengan benar.');
          return;
        }

        setParsedCustomerData(validData);
      },
      error: (error) => {
        setError('Gagal membaca file CSV. Pastikan format file benar.');
        console.error('CSV Parse Error:', error);
      }
    });
    e.target.value = null;
  };

  const handleConfirmCustomerImport = async () => {
    setIsImporting(true);
    setError('');
    setMessage('');

    let addedCount = 0;
    let failedCount = 0;
    const failedCustomers = [];

    for (const customerData of parsedCustomerData) {
      try {
        const fullCustomerData = {
          ...customerData,
          mitraUid: currentUser.uid,
          nama_mitra: currentUser.nama || currentUser.nama_mitra,
          kode_mitra: currentUser.kode_mitra
        };
        
        await addCustomer(fullCustomerData);
        addedCount++;
      } catch (error) {
        failedCount++;
        failedCustomers.push(customerData.nama_pelanggan);
        console.error(`Failed to import customer ${customerData.nama_pelanggan}:`, error);
      }
    }

    if (addedCount > 0) {
      setMessage(`${addedCount} pelanggan berhasil diimpor.`);
      await fetchCustomers(); // Refresh the customer list
    }
    
    if (failedCount > 0) {
      setError(`${failedCount} pelanggan gagal diimpor: ${failedCustomers.join(', ')}`);
    }

    setParsedCustomerData([]);
    setIsImporting(false);
    
    // Auto hide messages after 5 seconds
    setTimeout(() => {
      setMessage('');
      setError('');
    }, 5000);
  };

  const downloadCustomerTemplate = () => {
    const csvContent = "nama_pelanggan,no_wa,alamat\nJohn Doe,08123456789,Jl. Merdeka No. 123 RT 01/RW 02 Kelurahan ABC Kecamatan XYZ Kota Jakarta\nJane Smith,08234567890,Jl. Sudirman No. 456 RT 02/RW 03 Kelurahan DEF Kecamatan UVW Kota Bandung\nBudi Santoso,08345678901,Jl. Thamrin No. 789 RT 03/RW 04 Kelurahan GHI Kecamatan RST Kota Surabaya";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'template_daftar_pelanggan.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <FaUsers className="text-4xl text-blue-500" />
          <h1 className="text-3xl font-bold text-gray-800">Daftar Pelanggan</h1>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
        >
          <FaPlus />
          Tambah Pelanggan
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700 font-medium">{message}</p>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Section */}
      <div className="bg-green-50 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-green-800 mb-2 flex items-center">
              <FaUpload className="mr-2" />
              Import Pelanggan dari CSV
            </h3>
            <p className="text-sm text-green-600 mb-3">
              Import banyak pelanggan sekaligus menggunakan file CSV (format: nama_pelanggan, no_wa, alamat)
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleFileSelect} 
                className="hidden" 
                ref={fileInputRef} 
              />
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              >
                <FaUpload /> Pilih File CSV
              </button>
              <button
                type="button"
                onClick={downloadCustomerTemplate}
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              >
                <FaDownload /> Download Template
              </button>
            </div>
          </div>
        </div>
        
        {parsedCustomerData.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center">
              <FaUsers className="mr-2" />
              Pratinjau Data Import ({parsedCustomerData.length} pelanggan)
            </h4>
            <div className="overflow-x-auto max-h-60 border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-green-100">
                  <tr>
                    <th className="p-3 text-left font-medium text-green-900">Nama Pelanggan</th>
                    <th className="p-3 text-left font-medium text-green-900">No WhatsApp</th>
                    <th className="p-3 text-left font-medium text-green-900">Alamat</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedCustomerData.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-t hover:bg-green-50">
                      <td className="p-3 font-medium">{row.nama_pelanggan}</td>
                      <td className="p-3">{row.no_wa}</td>
                      <td className="p-3 max-w-xs truncate" title={row.alamat}>{row.alamat}</td>
                    </tr>
                  ))}
                  {parsedCustomerData.length > 5 && (
                    <tr>
                      <td colSpan="3" className="p-3 text-center text-gray-500 text-xs bg-gray-50">
                        ... dan {parsedCustomerData.length - 5} pelanggan lainnya
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button 
                onClick={() => setParsedCustomerData([])} 
                className="text-gray-600 hover:text-gray-800 font-medium py-2 px-4 rounded-lg text-sm transition-colors"
              >
                Batal
              </button>
              <button 
                onClick={handleConfirmCustomerImport} 
                disabled={isImporting}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2"
              >
                {isImporting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Mengimpor...
                  </>
                ) : (
                  <>
                    <FaPlus />
                    Import {parsedCustomerData.length} Pelanggan
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nama Pelanggan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  No WhatsApp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alamat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tanggal Ditambahkan
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.length > 0 ? customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FaUsers className="text-gray-400 mr-3" />
                      <div className="text-sm font-medium text-gray-900">
                        {customer.nama}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FaPhone className="text-gray-400 mr-3" />
                      <div className="text-sm text-gray-900">{customer.no_wa}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="text-gray-400 mr-3 mt-1" />
                      <div className="text-sm text-gray-900 max-w-xs">
                        {customer.alamat}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString('id-ID') : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(customer)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Hapus"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p>Belum ada data pelanggan</p>
                    <button
                      onClick={openAddModal}
                      className="mt-2 text-blue-600 hover:text-blue-800"
                    >
                      Tambah pelanggan pertama
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      <Modal isOpen={isModalOpen} onClose={resetForm}>
        <div className="p-6 max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <FaUsers className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {editingCustomer ? 'Edit Pelanggan' : 'Tambah Pelanggan Baru'}
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              {editingCustomer ? 'Perbarui informasi pelanggan' : 'Masukkan data pelanggan baru'}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <FaUsers className="inline mr-2 text-gray-400" />
                Nama Pelanggan *
              </label>
              <input
                type="text"
                value={formData.nama_pelanggan}
                onChange={(e) => {
                  setFormData({ ...formData, nama_pelanggan: e.target.value });
                  if (formErrors.nama_pelanggan) {
                    setFormErrors({ ...formErrors, nama_pelanggan: '' });
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 ${
                  formErrors.nama_pelanggan 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Contoh: John Doe"
                required
              />
              {formErrors.nama_pelanggan && (
                <p className="text-red-500 text-xs mt-1">{formErrors.nama_pelanggan}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <FaPhone className="inline mr-2 text-gray-400" />
                Nomor WhatsApp *
              </label>
              <input
                type="tel"
                value={formData.no_wa}
                onChange={(e) => {
                  setFormData({ ...formData, no_wa: e.target.value });
                  if (formErrors.no_wa) {
                    setFormErrors({ ...formErrors, no_wa: '' });
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 ${
                  formErrors.no_wa 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Contoh: 08123456789"
                required
              />
              {formErrors.no_wa ? (
                <p className="text-red-500 text-xs mt-1">{formErrors.no_wa}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Format: 08xxxxxxxxxx</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                Alamat Lengkap *
              </label>
              <textarea
                value={formData.alamat}
                onChange={(e) => {
                  setFormData({ ...formData, alamat: e.target.value });
                  if (formErrors.alamat) {
                    setFormErrors({ ...formErrors, alamat: '' });
                  }
                }}
                rows="4"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 resize-none ${
                  formErrors.alamat 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Contoh: Jl. Merdeka No. 123, RT 01/RW 02, Kelurahan ABC, Kecamatan XYZ, Kota/Kabupaten, Provinsi"
                required
              />
              {formErrors.alamat ? (
                <p className="text-red-500 text-xs mt-1">{formErrors.alamat}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Masukkan alamat selengkap mungkin</p>
              )}
            </div>

            <div className="flex space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={resetForm}
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Menyimpan...
                  </>
                ) : (
                  editingCustomer ? 'Perbarui Data' : 'Simpan Data'
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default DaftarPelanggan;
